import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Button, Card, Carousel, Badge } from 'react-bootstrap';
import { CartContext } from '../contexts/CartContext';
import { 
    FaWhatsapp, FaCheck, FaTruck, FaBuilding, FaSearch, 
    FaMapMarkerAlt, FaStar, FaHeadset, FaGoogle 
} from 'react-icons/fa';
import SearchBar from './SearchBar';
import { Helmet } from 'react-helmet';
import './Materiales.css';

// Importamos solo lo necesario desde materialesData
import { materiales, subrubroNames, zonasCobertura, ITEMS_PER_PAGE } from '../data/materialesData';

// --------------------------------------------------------
// DATOS ESTÁTICOS ACTUALIZADOS LOCALMENTE
// --------------------------------------------------------
const suppliers = [
    { src: '/logolomanegra.png', alt: 'Loma Negra', featured: true },
    { src: '/LOGOSIKA.png', alt: 'Sika' },
    { src: '/logoacindarofi.png', alt: 'Red Acindar' }, 
    { src: '/logodafre.jpg', alt: 'Dafre' },
    { src: '/logo varsovia.jpg', alt: 'Grupo Varsovia' },
    { src: '/LOGOTECMA.PNG', alt: 'Tecma' },
    { src: '/logoeleprint.png', alt: 'Eleprint' },
    { src: '/logogoldir.png', alt: 'Goldir' },
    { src: '/dycasalogo.jpg', alt: 'Ieb Construcciones' },
    { src: '/logoaubasa.png', alt: 'Aubasa' },
    { src: '/logopfisterer.png', alt: 'Pfisterer' },
    { src: '/logoweber.png', alt: 'Weber' },
    { src: '/logofanelli.png', alt: 'Fanelli' },
    { src: '/posesalogo.jpg', alt: 'Pose sa' },
    { src: '/logoctibor.png', alt: 'Ctibor' }
];

// Reseñas 100% reales extraídas de Google Maps
const testimonios = [
    { 
        text: "Que decir? Desde el 1er momento la atención fue de 10! Dudas, preguntas todo y Martín siempre bien predispuesto! Se acordó día hora y salio más que bien! Una logística impecable!", 
        author: "Diego nf02", rating: 5 
    },
    { 
        text: "Excelente. Desde el sector de ventas, logística y técnico es simplemente impecable, super profesionales. Todo es detallado y prolijo desde el principio hasta el final.", 
        author: "Fernando Miquelarena", rating: 5 
    },
    { 
        text: "La verdad excelente servicio, desde la venta, la entrega y hasta la post venta. Unos genios, una empresa seria que estan atentos y responden todos los msj. Los recomiendo siempre.", 
        author: "Suena El Nacho", rating: 5 
    },
    { 
        text: "Quería agradecer a todo el equipo de DAROM. Desde Martin por la atención telefónica hasta todo el personal afectado al servicio de descarga. ¡Excelente todo, muchas gracias!", 
        author: "Marcelo Diaz", rating: 5 
    },
    { 
        text: "Excelente atención en Darom. Ivan súper atento en todo momento, materiales de calidad y entregas organizadas. Muy conformes con la compra.", 
        author: "Nara Larrosa", rating: 5 
    },
    { 
        text: "Estoy terminando mi casa, y el 100% de los materiales, hormigón y movimiento de suelos lo hice con ellos. El nivel de servicio que tienen es excelente.", 
        author: "Roman Sapia", rating: 5 
    },
    { 
        text: "Excelente servicio, puntuales y sobre todo con buena predisposición al momento de la obra. Saludos y hasta la próxima!", 
        author: "Santiago Sanchez benz", rating: 5 
    },
    { 
        text: "Excelente atención y servicio. Un poco ajustado el cálculo del hormigón, pero lo solucionaron con una rapidez increíble. Muy recomendable!! Calidad, cordialidad.", 
        author: "patri desvoignes", rating: 5 
    },
    { 
        text: "Excelente servicio. Desde la compra hasta la entrega. El envío fue rapidísimo y siempre sabiendo en qué momento despachan camiones! Un lujo.", 
        author: "vicentini aldana", rating: 5 
    },
    { 
        text: "Super recomiendo, excelente servicio y atención. Fui atendida por una chica llamada Angeles, súper predispuesta en todo momento, me asesoró muy bien.", 
        author: "Natalia Saux", rating: 5 
    },
    { 
        text: "Excelente servicio de parte de todo el equipo de Darom, desde la recepción del pedido hasta su finalización. Super amables y predispuestos a responder todas mis dudas.", 
        author: "Daniel Bobadilla", rating: 5 
    }
];

// Función para normalizar búsqueda
const normalizeString = (str) => 
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// --------------------------------------------------------
// COMPONENTES PUROS
// --------------------------------------------------------
const GoogleRatingBadge = () => (
    <div className="d-flex justify-content-center w-100 mt-4 mb-2">
        <a 
            href="#reviews" 
            className="d-inline-flex align-items-center text-decoration-none py-2 px-4 shadow-lg bg-white rounded-pill" 
            style={{ transition: 'transform 0.2s', width: 'auto', whiteSpace: 'nowrap' }}
        >
            <FaGoogle className="text-danger me-2" size={20} />
            <div className="d-flex align-items-center mx-2 border-end border-secondary border-opacity-25 pe-3">
                <span className="fw-bold fs-5 me-1" style={{ color: '#212529' }}>4.7</span>
                <FaStar className="text-warning" size={18} />
            </div>
            <span className="small ms-2 fw-bold" style={{ color: '#495057' }}>50+ Reseñas Verificadas</span>
        </a>
    </div>
);

// --------------------------------------------------------
// COMPONENTE PRINCIPAL
// --------------------------------------------------------
function Materiales() {
    const { addToCart } = useContext(CartContext);
    const [selectedSubrubro, setSelectedSubrubro] = useState(localStorage.getItem('selectedSubrubro') || 'hidrofugos');
    const [visibleProducts, setVisibleProducts] = useState(ITEMS_PER_PAGE);
    const [searchQuery, setSearchQuery] = useState('');
    const [clientType, setClientType] = useState('minorista');
    const [isLoading, setIsLoading] = useState(false);
    const [animateHero, setAnimateHero] = useState(false);
    const productGridRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const heroTimeout = setTimeout(() => setAnimateHero(true), 100);
        return () => clearTimeout(heroTimeout);
    }, []);

    useEffect(() => {
        localStorage.setItem('selectedSubrubro', selectedSubrubro);
    }, [selectedSubrubro]);

    const handleSubrubroSelect = (subrubro) => {
        setIsLoading(true);
        setSelectedSubrubro(subrubro);
        setVisibleProducts(ITEMS_PER_PAGE);
        setSearchQuery('');
        
        const loadingTimeout = setTimeout(() => {
            setIsLoading(false);
            if (productGridRef.current) {
                productGridRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 300);
        return () => clearTimeout(loadingTimeout);
    };

    const handleLoadMore = () => {
        setIsLoading(true);
        const loadMoreTimeout = setTimeout(() => {
            setVisibleProducts((prev) => prev + ITEMS_PER_PAGE);
            setIsLoading(false);
        }, 500);
        return () => clearTimeout(loadMoreTimeout);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        setVisibleProducts(ITEMS_PER_PAGE);
    };

    const handleAddToCart = (material) => {
        addToCart(material);
    };

    const filteredProducts = useMemo(() => {
        return materiales[selectedSubrubro]?.filter(material =>
            normalizeString(material.name).includes(normalizeString(searchQuery.trim()))
        ) || [];
    }, [selectedSubrubro, searchQuery]);

    const mainPhone = "542215739000";

    const getWhatsappLink = () => {
        const baseMessage = "Hola Darom SA, vi su página web y me interesa solicitar información";
        const finalMessage = clientType === 'mayorista' 
            ? `${baseMessage} para compras mayoristas` 
            : baseMessage;
            
        return `https://wa.me/${mainPhone}?text=${encodeURIComponent(finalMessage)}`;
    };

    const renderCarouselItems = (items) => {
        const itemsPerSlide = 4;
        return Array.from({ length: Math.ceil(items.length / itemsPerSlide) }, (_, i) => (
            <Carousel.Item key={i}>
                <Row className="justify-content-center align-items-center">
                    {items.slice(i * itemsPerSlide, i * itemsPerSlide + itemsPerSlide).map((item, idx) => (
                        <Col md={3} sm={6} xs={6} key={idx} className="mb-2">
                            <Card className={`supplier-card text-center border-0 bg-transparent ${item.featured ? 'featured-supplier' : ''}`}>
                                <Card.Img 
                                    variant="top" 
                                    src={item.src} 
                                    alt={item.alt} 
                                    className="supplier-logo img-fluid" 
                                    loading="lazy" 
                                    onError={(e) => { e.target.src = '/placeholder-logo.png'; }}
                                    style={{ 
                                        maxHeight: '80px', 
                                        objectFit: 'contain',
                                        transition: 'all 0.3s ease'
                                    }}
                                />
                                {item.featured && (
                                    <div className="featured-badge">
                                        <FaStar size={12} />
                                    </div>
                                )}
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Carousel.Item>
        ));
    };

    return (
        <div className="materiales-container">
            <Helmet>
                <title>Corralón en Zona Sur GBA | Materiales Mayoristas y Minoristas | Darom SA</title>
                <meta name="description" content="Corralón líder en zona sur del Gran Buenos Aires. Distribuidores mayoristas y minoristas de materiales de construcción. Precios competitivos, entrega rápida." />
                <meta name="keywords" content="corralón zona sur, materiales construcción mayorista, distribuidor materiales construcción, corralón GBA sur, materiales para construcción, cemento, hierros, ladrillos, precios mayoristas" />
                <link rel="canonical" href="https://daromsa.com.ar/services/materiales" />
                {/* BLOQUE SEO JSON-LD RESTAURADO */}
                <script type="application/ld+json">
                    {`{
                        "@context": "https://schema.org",
                        "@type": "HardwareStore",
                        "name": "Darom SA Corralón",
                        "description": "Distribuidor mayorista y minorista de materiales de construcción en zona sur del Gran Buenos Aires",
                        "url": "https://daromsa.com.ar",
                        "telephone": "+54-221-573-9000",
                        "address": {
                            "@type": "PostalAddress",
                            "addressRegion": "Buenos Aires",
                            "addressCountry": "AR"
                        },
                        "areaServed": "Zona Sur Gran Buenos Aires",
                        "priceRange": "$$",
                        "openingHours": "Mo-Fr 08:00-18:00, Sa 08:00-13:00"
                    }`}
                </script>
            </Helmet>

            {/* HERO SECTION */}
            <div className={`heroc-section ${animateHero ? 'animate-in' : ''}`} 
                style={{ backgroundImage: 'url(/portadacoralon.JPG)' }}>
                <Container>
                    <div className="heroc-content text-center">
                        <Row className="justify-content-center">
                            <Col lg={10} xl={9}>
                                <div className="mb-4 animate-delay-1 d-flex justify-content-center gap-2 flex-wrap">
                                    <Badge bg="danger" className="py-2 px-3 fs-6 badge-darom shadow-sm">VENTA MAYORISTA</Badge>
                                    <Badge bg="warning" text="dark" className="py-2 px-3 fs-6 shadow-sm">VENTA MINORISTA</Badge>
                                    <Badge bg="light" text="dark" className="py-2 px-3 fs-6 shadow-sm">ZONA SUR GBA</Badge>
                                </div>
                                
                                <h1 className="animate-delay-2">
                                    CORRALÓN EN ZONA SUR GBA<br />
                                    <span className="hero-highlight">MAYORISTA Y MINORISTA</span>
                                </h1>
                                
                                <p className="lead animate-delay-3">
                                    <strong>Distribuidores líderes</strong> de materiales para la construcción en zona sur del Gran Buenos Aires. 
                                    <br /><span className="hero-subtitle">Precios mayoristas exclusivos para corralones, constructoras y revendedores.</span>
                                </p>
                                
                                {/* Client Selector y Botón WhatsApp Principal */}
                                <div className="client-selector mb-4 animate-delay-4 bg-dark bg-opacity-50 p-4 rounded-4 border border-light border-opacity-25 backdrop-blur shadow-lg">
                                    <div className="text-center mb-3">
                                        <small className="text-light fw-bold tracking-wider">¿QUÉ TIPO DE PRESUPUESTO BUSCÁS?</small>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-center gap-3 mb-4">
                                        <Button
                                            variant={clientType === 'minorista' ? "danger" : "outline-light"}
                                            onClick={() => setClientType('minorista')}
                                            className={`fw-bold px-4 py-3 rounded-3 shadow-sm ${clientType !== 'minorista' && 'opacity-75'}`}
                                            style={{ flex: '1', maxWidth: '300px' }}
                                        >
                                            <FaBuilding className="me-2" size={20} />
                                            PROYECTO PARTICULAR
                                        </Button>
                                        <Button
                                            variant={clientType === 'mayorista' ? "warning" : "outline-light"}
                                            onClick={() => setClientType('mayorista')}
                                            className={`fw-bold px-4 py-3 rounded-3 shadow-sm ${clientType !== 'mayorista' && 'opacity-75'}`}
                                            style={{ flex: '1', maxWidth: '300px' }}
                                        >
                                            <FaTruck className="me-2" size={20} />
                                            COMPRA MAYORISTA
                                        </Button>
                                    </div>

                                    {/* Botones CTA en Hero - Botón de WSP Rojo */}
                                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-center gap-3 pt-3 border-top border-light border-opacity-25">
                                        <Button 
                                            size="lg"
                                            variant="danger"
                                            href={getWhatsappLink()}
                                            target="_blank"
                                            className="px-4 py-3 fw-bold shadow-lg d-flex align-items-center justify-content-center text-white"
                                        >
                                            <FaWhatsapp className="me-2" size={24} />
                                            {clientType === 'mayorista' ? 'COTIZACIÓN MAYORISTA' : 'SOLICITAR PRESUPUESTO'}
                                        </Button>
                                        
                                        <Button 
                                            variant="outline-light" 
                                            size="lg"
                                            href="#productos"
                                            className="px-4 py-3 fw-bold d-flex align-items-center justify-content-center"
                                        >
                                            <FaSearch className="me-2" />
                                            VER PRODUCTOS
                                        </Button>
                                    </div>
                                    
                                    {/* Validacion de Maps Centrada y sin estirarse */}
                                    <GoogleRatingBadge />
                                </div>

                                {/* Banner de Contacto Unificado */}
                                <div className="contact-info mt-4 p-3 animate-delay-8 bg-black bg-opacity-50 rounded-4 border border-light border-opacity-10 backdrop-blur shadow-lg">
                                    <Row className="align-items-center justify-content-center text-center">
                                        <Col>
                                            <div className="mb-1 d-flex justify-content-center align-items-center gap-2">
                                                <FaHeadset className="text-danger" size={16} />
                                                <span className="text-light fw-bold" style={{letterSpacing: '1px', fontSize: '0.9rem'}}>ATENCIÓN COMERCIAL CENTRALIZADA</span>
                                            </div>
                                            <a href={`tel:+${mainPhone}`} className="text-decoration-none">
                                                <div className="fw-bold display-6 text-white mb-1 contact-phone-hover" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)'}}>
                                                    +54 221 573-9000
                                                </div>
                                            </a>
                                            <div className="text-light opacity-75 small">Línea única para asesoramiento, ventas minoristas y cotizaciones mayoristas.</div>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>

            {/* PRODUCTOS SECTION */}
            <section id="productos" className="categories-section py-5 bg-light" ref={productGridRef}>
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="section-title display-5 fw-bold">Materiales de Construcción</h2>
                        <p className="lead text-muted fs-5">
                            Explorá nuestro catálogo interactivo y armá tu lista de cotización
                        </p>
                    </div>
                    
                    <div className="category-selector mb-5">
                        <div className="text-center mb-4">
                            <h5 className="category-subtitle">SELECCIONÁ UNA CATEGORÍA</h5>
                        </div>
                        <div className="d-flex flex-wrap justify-content-center gap-2">
                            {Object.keys(materiales).map((subrubro) => (
                                <Button
                                    key={subrubro}
                                    variant={selectedSubrubro === subrubro ? "danger" : "outline-dark"}
                                    onClick={() => handleSubrubroSelect(subrubro)}
                                    className="text-uppercase fw-bold px-4 py-2 category-btn rounded-pill"
                                >
                                    {subrubroNames[subrubro] || subrubro}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="search-bar-container mb-5 mx-auto" style={{maxWidth: '600px'}}>
                        <SearchBar onSearch={handleSearch} />
                    </div>

                    {isLoading && (
                        <div className="text-center py-5">
                            <div className="spinner-border text-danger" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-3 text-muted fw-bold">Cargando catálogo...</p>
                        </div>
                    )}

                    {!isLoading && (
                        <Row className="g-4">
                            {filteredProducts.slice(0, visibleProducts).map((material) => (
                                <Col xs={12} sm={6} md={4} lg={3} key={material.id}>
                                    <Card className="product-card h-100 border-0">
                                        <div className="product-badge">STOCK DISPONIBLE</div>
                                        {clientType === 'mayorista' && (
                                            <div className="product-badge bg-warning text-dark" style={{ top: '45px' }}>
                                                PRECIO MAYORISTA
                                            </div>
                                        )}
                                        <div className="product-image-container overflow-hidden">
                                            <Card.Img 
                                                variant="top" 
                                                src={material.img} 
                                                alt={`Imagen de ${material.name}`} 
                                                loading="lazy" 
                                                onError={(e) => { e.target.src = '/placeholder-product.jpg'; }}
                                                className="product-image"
                                            />
                                        </div>
                                        <Card.Body className="d-flex flex-column p-4">
                                            <Card.Title className="product-title mb-3">{material.name}</Card.Title>
                                            <div className="mt-auto pt-3 border-top">
                                                <small className="text-success fw-bold d-block mb-3 d-flex align-items-center gap-2">
                                                    <FaCheck /> Habilitado para cotizar
                                                </small>
                                                <Button
                                                    variant="danger"
                                                    onClick={() => handleAddToCart(material)}
                                                    className="w-100 fw-bold py-2 btn-darom-primary d-flex justify-content-center align-items-center"
                                                >
                                                    Agregar a la Lista
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {!isLoading && visibleProducts < filteredProducts.length && (
                        <div className="text-center mt-5">
                            <Button 
                                variant="outline-danger" 
                                onClick={handleLoadMore} 
                                className="fw-bold px-5 py-3 rounded-pill btn-darom-outline text-danger border-danger"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Cargando...' : 'Cargar más productos'}
                            </Button>
                        </div>
                    )}

                    {!isLoading && filteredProducts.length === 0 && (
                        <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
                            <FaSearch size={40} className="text-muted mb-3 opacity-50" />
                            <h4 className="fw-bold text-dark">No se encontraron resultados</h4>
                            <p className="text-muted">No hay productos que coincidan con tu búsqueda en esta categoría.</p>
                        </div>
                    )}
                </Container>
            </section>

            {/* CTA SECTION */}
            <section className="cta-section py-5">
                <Container>
                    <Row className="text-center mb-5">
                        <Col>
                            <h2 className="text-dark mb-3 display-5 fw-bold">¿Necesitás cotizar rápidamente?</h2>
                            <p className="lead text-muted fs-5">Contactá a nuestros asesores comerciales por WhatsApp</p>
                        </Col>
                    </Row>
                    <Row className="g-4 justify-content-center">
                        <Col md={5}>
                            <Card className="h-100 cta-card text-center p-4 p-lg-5">
                                <Card.Body className="d-flex flex-column">
                                    <FaBuilding size={50} className="text-danger mb-4 mx-auto" />
                                    <h4 className="fw-bold mb-3">Obras Particulares</h4>
                                    <p className="text-muted mb-4 flex-grow-1">
                                        Atención personalizada para reformas, ampliaciones y construcciones familiares.
                                    </p>
                                    <Button 
                                        variant="danger" 
                                        size="lg"
                                        href={`https://wa.me/${mainPhone}?text=Hola%20Darom%20SA,%20necesito%20presupuesto%20para%20una%20obra%20particular`}
                                        target="_blank"
                                        className="fw-bold w-100 py-3 btn-darom-primary d-flex justify-content-center align-items-center"
                                    >
                                        <FaWhatsapp className="me-2" size={20} />
                                        CONSULTA MINORISTA
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={5}>
                            <Card className="h-100 cta-card text-center p-4 p-lg-5" style={{borderTopColor: '#ffc107'}}>
                                <Card.Body className="d-flex flex-column">
                                    <FaTruck size={50} className="text-warning mb-4 mx-auto" />
                                    <h4 className="fw-bold mb-3">Empresas y Revendedores</h4>
                                    <p className="text-muted mb-4 flex-grow-1">
                                        Condiciones exclusivas para constructoras, corralones y proyectos a gran escala.
                                    </p>
                                    <Button 
                                        variant="warning" 
                                        size="lg"
                                        href={`https://wa.me/${mainPhone}?text=Hola%20Darom%20SA,%20me%20interesa%20solicitar%20lista%20de%20precios%20para%20compra%20mayorista`}
                                        target="_blank"
                                        className="fw-bold w-100 py-3 d-flex justify-content-center align-items-center shadow-sm"
                                        style={{transition: 'all 0.3s ease'}}
                                    >
                                        <FaWhatsapp className="me-2" size={20} />
                                        COTIZACIÓN MAYORISTA
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* COVERAGE SECTION */}
            <section className="coverage-section py-5 bg-white border-top">
                <Container>
                    <Row className="align-items-center g-5">
                        <Col lg={6}>
                            <h2 className="section-title mb-4 display-5 fw-bold">Cobertura en Zona Sur GBA</h2>
                            <p className="lead text-muted mb-4">
                                <strong>Logística propia y servicio de distribución garantizado</strong> a las principales localidades de la zona sur.
                            </p>
                            <Row className="g-3">
                                {zonasCobertura.map((zona, index) => (
                                    <Col xs={6} sm={4} lg={6} key={index}>
                                        <div className="d-flex align-items-center p-3 bg-light rounded-3 border h-100">
                                            <FaMapMarkerAlt className="text-danger me-2 flex-shrink-0" />
                                            <span className="fw-semibold text-dark">{zona}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                        <Col lg={6}>
                            <div className="bg-light p-5 rounded-4 text-center border shadow-sm h-100 d-flex flex-column justify-content-center">
                                <div className="bg-white rounded-circle d-inline-flex p-4 mx-auto mb-4 shadow-sm">
                                    <FaTruck size={40} className="text-danger" />
                                </div>
                                <h4 className="fw-bold text-dark mb-3">¿No encontrás tu localidad?</h4>
                                <p className="text-muted mb-4 fs-6">
                                    Consultanos por cobertura en otras zonas del Gran Buenos Aires. 
                                    Evaluamos cada proyecto para brindar la mejor solución logística.
                                </p>
                                <Button 
                                    variant="outline-danger"
                                    size="lg"
                                    href={`https://wa.me/${mainPhone}?text=Hola%20Darom%20SA,%20quisiera%20consultar%20si%20realizan%20envíos%20a%20mi%20localidad`}
                                    target="_blank"
                                    className="fw-bold px-4 py-3 rounded-pill mx-auto"
                                    style={{maxWidth: '300px'}}
                                >
                                    CONSULTAR COBERTURA
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* SUPPLIERS CAROUSEL */}
            <section className="suppliers-section py-5 bg-light border-top">
                <Container>
                    <h2 className="section-title mb-5 text-center display-6 fw-bold">Marcas Líderes que Distribuimos</h2>
                    <Carousel indicators={false} controls={suppliers.length > 4} variant="dark" className="px-4">
                        {renderCarouselItems(suppliers)}
                    </Carousel>
                </Container>
            </section>

            {/* TESTIMONIALS */}
            <section id="reviews" className="testimonials-section py-5 bg-white border-top">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="section-title display-6 fw-bold mb-3">Confían en Nosotros</h2>
                        <div className="d-flex justify-content-center">
                            <div className="d-inline-flex align-items-center py-2 px-4 shadow-sm bg-light rounded-pill border">
                                <FaGoogle className="text-danger me-2" size={20} />
                                <div className="d-flex align-items-center mx-2 border-end border-secondary border-opacity-25 pe-3">
                                    <span className="rating-value text-dark fw-bold fs-5 me-1">4.7</span>
                                    <FaStar className="text-warning" size={18} />
                                </div>
                                <span className="text-muted small ms-2 fw-semibold">50+ Reseñas Verificadas</span>
                            </div>
                        </div>
                    </div>
                    <Row>
                        <Col lg={8} className="mx-auto">
                            <Carousel indicators={true} interval={6000} pause="hover" variant="dark" className="pb-5">
                                {testimonios.map((testimonio, index) => (
                                    <Carousel.Item key={index}>
                                        <div className="testimonial-card text-center p-4 p-md-5">
                                            <div className="rating mb-4">
                                                {[...Array(testimonio.rating)].map((_, i) => (
                                                    <FaStar key={i} className="text-warning fs-4 mx-1" />
                                                ))}
                                            </div>
                                            <blockquote className="testimonial-text fs-5 mb-4 fst-italic text-muted">
                                                "{testimonio.text}"
                                            </blockquote>
                                            <div className="testimonial-author">
                                                <strong className="fs-5 text-dark">{testimonio.author}</strong>
                                                <div className="text-success small fw-bold mt-1">
                                                    <FaCheck className="me-1" /> Cliente Verificado
                                                </div>
                                            </div>
                                        </div>
                                    </Carousel.Item>
                                ))}
                            </Carousel>
                            
                            <div className="text-center mt-2">
                                <Button 
                                    variant="outline-dark" 
                                    size="lg"
                                    href="https://g.page/r/CYU8aD9k5psMEAE/review" 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="fw-bold px-4 rounded-pill"
                                >
                                    <FaGoogle className="me-2 text-danger" /> Dejar Mi Reseña
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default Materiales;